
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useClose } from "@/contexts/CloseContext";
import { CloseApiService } from "@/services/closeApi/closeApiService";
import { Loader2, RefreshCw, AlertCircle, ListChecks, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export const CloseTasks = () => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isConnected, apiKey } = useClose();

  const fetchTasks = async () => {
    if (!isConnected || !apiKey) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await CloseApiService.getTasks(apiKey);
      setTasks(data);
    } catch (err: any) {
      console.error("Error fetching Close tasks:", err);
      setError(err.message || "Fehler beim Laden der Aufgaben");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected && apiKey) {
      fetchTasks();
    }
  }, [isConnected, apiKey]);

  if (!isConnected) {
    return (
      <Card className="bg-gray-800/20 border-gray-700">
        <CardContent className="p-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Nicht verbunden</h3>
          <p className="text-gray-400 mb-4">
            Verbinden Sie Ihr Close CRM-Konto, um Ihre Aufgaben zu sehen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Aufgaben</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchTasks}
          disabled={isLoading}
          className="border-gray-700"
        >
          {isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
        </Button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-900 text-red-400 px-4 py-3 rounded flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : tasks.length > 0 ? (
        <div className="space-y-3">
          {tasks.map((task) => (
            <Card key={task.id} className="bg-gray-800/20 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {task.is_complete ? (
                      <CheckCircle size={16} className="text-green-500" />
                    ) : (
                      <ListChecks size={16} className="text-amber-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-sm">{task.text}</h4>
                      {task.due_date && (
                        <Badge className={`ml-2 ${task.is_complete ? 'bg-green-500/20 text-green-500' : 'bg-amber-500/20 text-amber-500'}`}>
                          Fällig: {format(new Date(task.due_date), 'dd.MM.yyyy', { locale: de })}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Zugewiesen an: {task.assigned_to_name}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-500">
          <ListChecks className="w-10 h-10 mx-auto mb-4 opacity-50" />
          <p>Keine Aufgaben gefunden</p>
        </div>
      )}
    </div>
  );
};
